/* eslint-disable  @typescript-eslint/no-explicit-any */ // AJB: remove
import AspectMultiMenu from "@/components/search/AspectMultiMenu";
import AspectRangeMenu from "@/components/search/AspectRangeMenu";
import AGE_OPTIONS from "@/constants/AgeOptions";
import HEIGHT_OPTIONS from "@/constants/HeightOptions";
import PRICE_OPTIONS from "@/constants/PriceOptions";
import HomePageSearchRequest from "@/models/HomePageSearchRequest";
import { HomePageSearchResponse } from "@/models/HomePageSearchResponse";
import { Box, CircularProgress, Typography } from "@mui/material";
import HeightIcon from "@mui/icons-material/Height";
import PersonIcon from "@mui/icons-material/Person";
import ChecklistIcon from "@mui/icons-material/Checklist";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import ColorLensOutlinedIcon from "@mui/icons-material/ColorLensOutlined";
import FemaleIcon from "@mui/icons-material/Female";
import MaleIcon from "@mui/icons-material/Male";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { TOP_NAV_Z_INDEX } from "@/app/layout/GlobalNav";
import {
  BOX_SHADOW_SIZE_MEDIUM,
  HIDE_SCROLLBAR_STYLE,
  NAV_SELECTED_BACKGROUND_COLOR,
  PRIMARY_COLOR,
} from "@/app/layout/LayoutConstants";
import AspectToggle from "@/components/search/AspectToggle";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import AspectText from "@/components/search/AspectText";
import { useCallback, useEffect, useRef } from "react";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import PillButton from "@/app/shared/PillButton";
import DEFAULT_SEARCH_REQUEST from "@/constants/DefaultSearchRequest";
import ClearIcon from "@mui/icons-material/Clear";
import { BoxWithGutters } from "@/components/common/BoxWithGutters";
import { usePathname } from "next/navigation";
import SubNav from "@/app/layout/SubNav";
import AspectLocationMenu from "@/components/search/AspectLocationMenu";
import CheckIcon from "@mui/icons-material/Check";
import { useTopNavHeadroom } from "@/app/layout/useTopNavHeadroom";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

let HORIZONTAL_SCROLL_POSITION_TO_RESTORE = 0;

export default function FiltersSection({
  searchResponse,
  searchRequest,
  onSearchRequestChange,
  isExpanded,
  setIsExpanded,
  isLoading,
}: {
  searchResponse: HomePageSearchResponse;
  searchRequest: HomePageSearchRequest;
  onSearchRequestChange: (updates: any, skipRefresh: boolean) => void;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  isLoading?: boolean;
}) {
  const horizontalSrollRef = useRef<HTMLElement>(null);
  const pathname = usePathname();
  const headroom = useTopNavHeadroom();

  headroom.registerCondition({
    key: "search-filters-expanded",
    proposition: !isExpanded,
  });

  const handleOnSearchRequestChange = useCallback(
    (updates: any, skipRefresh: boolean) => {
      HORIZONTAL_SCROLL_POSITION_TO_RESTORE =
        horizontalSrollRef.current?.scrollLeft ?? 0;
      onSearchRequestChange(updates, skipRefresh);
    },
    [onSearchRequestChange]
  );

  const handleSearchQueryChange = useCallback(
    (searchQuery: string) => {
      handleOnSearchRequestChange({ searchQuery }, false);
    },
    [handleOnSearchRequestChange]
  );

  useEffect(() => {
    if (HORIZONTAL_SCROLL_POSITION_TO_RESTORE) {
      horizontalSrollRef.current?.scroll(
        HORIZONTAL_SCROLL_POSITION_TO_RESTORE,
        0
      );
      HORIZONTAL_SCROLL_POSITION_TO_RESTORE = 0;
    }
  }, [pathname]);

  return (
    <SubNav collapseOnScroll>
      <Box
        sx={{
          display: "flex",
          flexDirection: isExpanded ? "column" : "row",
          position: "relative",
        }}
      >
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: TOP_NAV_Z_INDEX - 5,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress
              sx={{
                color: "black",
              }}
            />
          </Box>
        )}
        <BoxWithGutters
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: isExpanded ? "wrap" : "nowrap",
            paddingTop: 2.5,
            paddingBottom: 2,
            backgroundColor: NAV_SELECTED_BACKGROUND_COLOR,
            overflowX: "auto",
            width: "100%",
            ...HIDE_SCROLLBAR_STYLE,
          }}
          ref={horizontalSrollRef}
        >
          <AspectMultiMenu
            label="Category"
            options={searchResponse.categories}
            selectedValues={searchRequest.categories}
            onChange={(categories, skipRefresh) =>
              onSearchRequestChange({ categories }, skipRefresh)
            }
            icon={<CategoryOutlinedIcon />}
            isFilterBarExpanded={isExpanded}
          />
          <AspectMultiMenu
            label="Breed"
            options={searchResponse.breeds}
            selectedValues={searchRequest.breeds}
            onChange={(breeds, skipRefresh) =>
              handleOnSearchRequestChange({ breeds }, skipRefresh)
            }
            icon={<WaterDropOutlinedIcon />}
            isFilterBarExpanded={isExpanded}
          />
          <AspectMultiMenu
            label="Discipline"
            options={searchResponse.disciplines}
            selectedValues={searchRequest.disciplines}
            onChange={(disciplines, skipRefresh) =>
              handleOnSearchRequestChange({ disciplines }, skipRefresh)
            }
            icon={<ChecklistIcon />}
            isFilterBarExpanded={isExpanded}
          />
          <AspectLocationMenu
            searchRequest={searchRequest}
            searchResponse={searchResponse}
            handleOnSearchRequestChange={handleOnSearchRequestChange}
            isExpanded={isExpanded}
          />
          <AspectRangeMenu
            aspectKey="price"
            aspectLabel="Price"
            options={PRICE_OPTIONS}
            value={searchRequest.price}
            onChange={(price) => handleOnSearchRequestChange({ price }, true)}
            icon={<AttachMoneyIcon />}
            isFilterBarExpanded={isExpanded}
          />
          {searchResponse.isHorseVertical && (
            <>
              <AspectRangeMenu
                aspectKey="height"
                aspectLabel="Height"
                options={HEIGHT_OPTIONS}
                value={searchRequest.height}
                onChange={(height) =>
                  handleOnSearchRequestChange({ height }, true)
                }
                icon={<HeightIcon />}
                isFilterBarExpanded={isExpanded}
              />
              <AspectRangeMenu
                aspectKey="age"
                aspectLabel="Age"
                options={AGE_OPTIONS}
                value={searchRequest.age}
                onChange={(age) => handleOnSearchRequestChange({ age }, true)}
                icon={<HourglassEmptyOutlinedIcon />}
                isFilterBarExpanded={isExpanded}
              />
            </>
          )}
          <AspectMultiMenu
            label="Rider Level"
            options={searchResponse.riderLevels}
            selectedValues={searchRequest.riderLevels}
            onChange={(riderLevels, skipRefresh) =>
              handleOnSearchRequestChange({ riderLevels }, skipRefresh)
            }
            icon={<PersonIcon />}
            isFilterBarExpanded={isExpanded}
          />
          <AspectMultiMenu
            label="Colour"
            options={searchResponse.colours}
            selectedValues={searchRequest.colours}
            onChange={(colours, skipRefresh) =>
              handleOnSearchRequestChange({ colours }, skipRefresh)
            }
            icon={<ColorLensOutlinedIcon />}
            isFilterBarExpanded={isExpanded}
          />
          <AspectMultiMenu
            label="Gender"
            options={searchResponse.genders}
            selectedValues={searchRequest.genders}
            onChange={(genders, skipRefresh) =>
              handleOnSearchRequestChange({ genders }, skipRefresh)
            }
            icon={
              <>
                <FemaleIcon />
                <MaleIcon />
              </>
            }
            isFilterBarExpanded={isExpanded}
          />
          {(!!searchRequest.isUrgent || searchResponse.urgentAdsCount > 0) && (
            <AspectToggle
              label="Urgent"
              icon={<WhatshotIcon />}
              isSelected={!!searchRequest.isUrgent}
              onChange={(isUrgent) =>
                handleOnSearchRequestChange(
                  { isUrgent: isUrgent || null },
                  false
                )
              }
            />
          )}
          <AspectText
            value={searchRequest.searchQuery}
            onChange={handleSearchQueryChange}
          />
          {searchResponse.selectedValuesCount > 0 && (
            <PillButton
              isSelected
              sx={{
                backgroundColor: "transparent",
              }}
              onClick={() =>
                onSearchRequestChange(
                  {
                    ...DEFAULT_SEARCH_REQUEST,
                    vertical: searchRequest.vertical,
                  },
                  false
                )
              }
              startIcon={<ClearIcon />}
            >
              Clear All
            </PillButton>
          )}
        </BoxWithGutters>
      </Box>
      <PillButton
        primary
        sx={{
          position: "absolute",
          bottom: isExpanded ? "-50px" : "-30px",
          left: "50%",
          transform: "translateX(-50%)",
          boxShadow: BOX_SHADOW_SIZE_MEDIUM,
          backgroundColor: isExpanded ? "none" : "black",
        }}
        startIcon={isExpanded ? <CheckIcon /> : <ExpandMoreIcon />}
        endIcon={
          searchResponse.selectedValuesCount &&
          !isExpanded && (
            <Typography
              sx={{
                color: "white",
                background: PRIMARY_COLOR,
                borderRadius: "100%",
                width: "25px",
                height: "25px",
                fontSize: "16px !important",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginRight: "-12px",
              }}
            >
              {searchResponse.selectedValuesCount}
            </Typography>
          )
        }
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? "Show Results" : "Filters"}
      </PillButton>
    </SubNav>
  );
}
